import { useTranslation } from "react-i18next";
import { 
  Shield, 
  Laptop, 
  HeartHandshake, 
  Bus, 
  Smartphone, 
  Users,
  Phone,
  ExternalLink
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const supportServices = [
  {
    id: "domestic_violence",
    icon: Shield,
    title: "Domestic Violence Support",
    description: "Emergency shelters, crisis hotlines, legal advocacy, and counseling for survivors of domestic violence.",
    supportiveMessage: "You deserve to feel safe. Help is available 24/7.",
    color: "bg-purple-100 text-purple-700 border-purple-200",
    bgColor: "bg-purple-50",
    resources: [
      { name: "Day One Crisis Hotline", phone: "1-866-223-1111", available: "24/7" },
      { name: "National DV Hotline", phone: "1-800-799-7233", available: "24/7" },
      { name: "Alexandra House", phone: "(763) 780-2330", available: "24/7 shelter" },
      { name: "Women's Advocates", phone: "(651) 227-8284", available: "St. Paul area" }
    ]
  },
  {
    id: "digital_access",
    icon: Laptop,
    title: "Digital Access",
    description: "Free computers, internet access, and digital literacy training to help you stay connected and job-ready.",
    supportiveMessage: "Staying connected opens doors. These resources can help you get online.",
    color: "bg-cyan-100 text-cyan-700 border-cyan-200",
    bgColor: "bg-cyan-50",
    resources: [
      { name: "Hennepin County Library", info: "Free WiFi & computers at all locations" },
      { name: "Ramsey County Library", info: "Free computers & digital classes" },
      { name: "PCs for People", phone: "(651) 354-2552", info: "$15/month internet" },
      { name: "Literacy Minnesota", phone: "(651) 251-9110", info: "Digital skills training" }
    ]
  },
  {
    id: "recovery",
    icon: HeartHandshake,
    title: "Recovery Support",
    description: "Peer support groups, addiction recovery meetings, and counseling for substance use and mental health.",
    supportiveMessage: "Recovery is possible. You don't have to do this alone.",
    color: "bg-teal-100 text-teal-700 border-teal-200",
    bgColor: "bg-teal-50",
    resources: [
      { name: "AA Hotline (Twin Cities)", phone: "(952) 922-0880", available: "24/7" },
      { name: "NA Minnesota", phone: "(877) 767-6276", available: "24/7" },
      { name: "Minnesota Recovery Connection", phone: "(612) 584-4158", info: "Peer coaching" },
      { name: "SMART Recovery", info: "Science-based meetings", website: "smartrecovery.org" }
    ]
  },
  {
    id: "transportation",
    icon: Bus,
    title: "Transportation",
    description: "Reduced fare transit passes, ride assistance, and transportation planning to get you where you need to go.",
    supportiveMessage: "Getting around shouldn't be a barrier. These programs can help.",
    color: "bg-sky-100 text-sky-700 border-sky-200",
    bgColor: "bg-sky-50",
    resources: [
      { name: "Metro Transit TAP", phone: "(612) 373-3333", info: "Reduced fares for low income" },
      { name: "Metro Mobility", phone: "(651) 602-1111", info: "Disability services" },
      { name: "Redemption Project", phone: "(612) 293-7008", info: "TAP cards for reentry" },
      { name: "NEMT (Medical)", phone: "(866) 467-1724", info: "Free rides to appointments" }
    ]
  },
  {
    id: "communication",
    icon: Smartphone,
    title: "Communication Access",
    description: "Free government phones through Lifeline program for eligible individuals.",
    supportiveMessage: "A phone keeps you connected to jobs, family, and support. You may qualify for a free phone.",
    color: "bg-violet-100 text-violet-700 border-violet-200",
    bgColor: "bg-violet-50",
    resources: [
      { name: "Assurance Wireless", phone: "(888) 321-5880", info: "Free phone & data" },
      { name: "Life Wireless", phone: "(888) 543-3620", info: "Unlimited calls & texts" },
      { name: "Hennepin County", phone: "(612) 596-1300", info: "Help with Lifeline signup" }
    ]
  },
  {
    id: "community",
    icon: Users,
    title: "Community & Mentorship",
    description: "One-on-one mentoring, peer support networks, and community programs for successful reentry.",
    supportiveMessage: "Having someone in your corner makes a difference. These mentors understand.",
    color: "bg-lime-100 text-lime-700 border-lime-200",
    bgColor: "bg-lime-50",
    resources: [
      { name: "Amicus Reconnect (VOA)", phone: "(612) 870-7655", info: "1-on-1 mentoring" },
      { name: "NAMI Minnesota", phone: "(651) 645-2948", info: "Peer support groups" },
      { name: "GEO Reentry Connect", info: "Peer mentoring by people with reentry experience" },
      { name: "Goodwill-Easter Seals", phone: "(651) 379-5800", info: "Reentry mentoring" }
    ]
  }
];

const SupportServices = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#fafdff]" data-testid="support-services-page">
      {/* Header */}
      <div className="bg-[#0e3d69] border-b-4 border-[#7cafde]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3 mb-2">
            <HeartHandshake className="w-6 h-6 text-[#7cafde]" />
            <h1 className="text-2xl font-bold text-[#fafdff]">Support Services</h1>
          </div>
          <p className="text-[#fafdff]/80 max-w-2xl">
            Additional support services to help with recovery, safety, communication, transportation, and community connections.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid md:grid-cols-2 gap-6">
          {supportServices.map((service) => (
            <Card 
              key={service.id} 
              className={`border-2 ${service.color.split(' ')[2]} hover:shadow-lg transition-all duration-200`}
              data-testid={`service-card-${service.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${service.color}`}>
                    <service.icon className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0e3d69] text-lg">{service.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                  </div>
                </div>
                
                {/* Supportive Message */}
                <p className="text-sm italic text-slate-500 mb-4 pl-4 border-l-2 border-slate-200">
                  {service.supportiveMessage}
                </p>
                
                {/* Resources List */}
                <div className={`${service.bgColor} rounded-lg p-4`}>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Resources & Contacts</p>
                  <div className="space-y-3">
                    {service.resources.map((resource, idx) => (
                      <div key={idx} className="flex items-start justify-between gap-2">
                        <div>
                          <span className="text-slate-700 font-medium text-sm">{resource.name}</span>
                          {resource.info && (
                            <p className="text-slate-500 text-xs">{resource.info}</p>
                          )}
                          {resource.available && (
                            <p className="text-slate-500 text-xs">{resource.available}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {resource.phone ? (
                            <a 
                              href={`tel:${resource.phone}`} 
                              className="text-[#0e3d69] font-semibold text-sm hover:underline flex items-center gap-1 whitespace-nowrap"
                            >
                              <Phone className="w-3 h-3" />
                              {resource.phone}
                            </a>
                          ) : resource.website ? (
                            <a 
                              href={`https://${resource.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#0e3d69] text-sm hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Website
                            </a>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Crisis Banner */}
        <div className="mt-10 p-6 bg-red-50 border-2 border-red-200 rounded-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-red-800">In Crisis? Get Help Now</h3>
                <p className="text-red-600 text-sm">You matter. 24/7 crisis lines are available and confidential.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="tel:988" className="bg-red-600 text-white font-bold px-6 py-2 rounded-lg hover:bg-red-700 transition-colors text-center">
                988 (Mental Health)
              </a>
              <a href="tel:1-800-799-7233" className="bg-purple-600 text-white font-bold px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors text-center">
                DV Hotline
              </a>
            </div>
          </div>
        </div>

        {/* Supportive Footer Message */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm max-w-xl mx-auto">
            Taking the first step is often the hardest part. These organizations are here to help, 
            not judge. Reach out when you're ready.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportServices;
